/**
 * Parses an ISO date string as a local date, avoiding timezone offset issues.
 *
 * JavaScript's `new Date('2025-01-25')` interprets the string as UTC midnight and converts
 * to local time, which can result in the previous day in timezones behind UTC.
 *
 * This function parses the date components and creates a Date object at local midnight,
 * ensuring "2025-01-25" displays as January 25 regardless of timezone.
 *
 * Accepts both simple date format (YYYY-MM-DD) and full ISO 8601 datetime strings
 * (e.g., 2025-01-25T12:00:00Z or 2025-01-25T21:00:00-08:00). The time portion is ignored.
 *
 * @param dateStr - ISO date string in YYYY-MM-DD format or ISO 8601 datetime string
 * @returns Date object representing local midnight on the specified date
 *
 * @example
 * // In Pacific Time (UTC-8):
 * new Date('2025-01-25') // → Jan 24, 4:00 PM (WRONG - off by one day)
 * parseLocalDate('2025-01-25') // → Jan 25, 12:00 AM (CORRECT)
 * parseLocalDate('2025-01-25T21:00:00-08:00') // → Jan 25, 12:00 AM (CORRECT)
 */
export function parseLocalDate(dateStr: string): Date {
  if (!dateStr) {
    throw new Error('Date string is required')
  }

  // Extract just the date part (YYYY-MM-DD) from ISO datetime strings
  // Handles both 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:mm:ss...' formats
  const datePart = dateStr.split('T')[0]

  if (!datePart) {
    throw new Error(`Invalid date format: ${dateStr}. Expected YYYY-MM-DD or ISO 8601 datetime`)
  }

  const parts = datePart.split('-')
  if (parts.length !== 3) {
    throw new Error(`Invalid date format: ${dateStr}. Expected YYYY-MM-DD or ISO 8601 datetime`)
  }

  const year = Number(parts[0])
  const month = Number(parts[1])
  const day = Number(parts[2])

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    throw new Error(`Invalid date values in: ${dateStr}`)
  }

  return new Date(year, month - 1, day)
}

export function timeAgo(dateString: string): string {
  const now = new Date()
  const date = new Date(dateString)

  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) {
    return 'Just now'
  }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  }

  const days = Math.floor(hours / 24)
  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''} ago`
  }

  const weeks = Math.floor(days / 7)
  if (weeks < 4) {
    return `${weeks} week${weeks !== 1 ? 's' : ''} ago`
  }

  const months = Math.floor(days / 30) // ✅ approx months
  return `${months} month${months !== 1 ? 's' : ''} ago`
}
